// src/components/AddFollowUpModal.js
import React, { useState, useEffect } from "react";
import {
  TextField,
  Typography,
  MenuItem,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Paper,
} from "@mui/material";
import CrmDialog from "./CrmDialog";
import API from "../utils/api";
import { toast } from "react-toastify";
import { tSuccess, tError } from "../utils/toast";
import useLandscape from "../hooks/useLandscape";
import ModalHeader from "./ModalHeader";

export default function AddFollowUpModal({ open, onClose, onCreated }) {
  const isLandscape = useLandscape();
  const [leads, setLeads] = useState([]);
  const [form, setForm] = useState({
    lead: "",
    title: "",
    notes: "",
    due_date: "",
    
  });

  // Reset form
  const resetForm = () =>
    setForm({
      lead: "",
      title: "",
      notes: "",
      due_date: "",
      priority: "Normal",
    });

  // Load leads
  useEffect(() => {
    if (open) fetchLeads();
  }, [open]);

  const fetchLeads = async () => {
    try {
      const res = await API.get("/leads/");
      setLeads(res.data);
    } catch (err) {
      console.error("Failed to load leads", err);
    }
  };

  const handleChange = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!form.lead || !form.title) {
      toast.error("Please fill all required fields.");
      return;
    }

    try {
      await API.post("/followups/", form);
      toast.success("Follow-up created!");
      onCreated?.();
      onClose();
      resetForm();
    } catch (err) {
      console.error(err);
      toast.error("Failed to create follow-up");
    }
  };

  return (
    <CrmDialog
      open={open}
      onClose={onClose}
      maxWidth={isLandscape ? "md" : "sm"}
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: "90vh",
          // Override CrmDialog's padding in landscape
          padding: isLandscape ? "0 !important" : undefined,
          "& > *": {
            padding: isLandscape ? "16px" : undefined,
          },
        },
      }}
    >
      <ModalHeader
  title="Add Followup"
  subtitle="Create a new lead follow up."
/>
      
      <DialogContent
        sx={{
          // Remove all padding in landscape to allow flex layout
          padding: isLandscape ? "0 !important" : undefined,
          paddingTop: isLandscape ? "0 !important" : undefined,
          paddingBottom: isLandscape ? "0 !important" : undefined,
          paddingLeft: isLandscape ? "0 !important" : undefined,
          paddingRight: isLandscape ? "0 !important" : undefined,
          // Flex row layout
          display: isLandscape ? "flex !important" : "block",
          flexDirection: isLandscape ? "row !important" : "column",
          gap: isLandscape ? 2 : 0,
          overflowY: "auto",
          pt: isLandscape ? 0 : 2, // Only padding-top in portrait
        }}
      >
        {isLandscape ? (
          <>
            {/* Left Column - add padding here instead */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                overflowY: "auto",
                p: 2, // Padding on the column itself
              }}
            >
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: "primary.main" }}>
                Follow-up Details
              </Typography>
              
              <TextField
                select
                fullWidth
                label="Lead"
                size="small"
                value={form.lead}
                onChange={(e) => handleChange("lead", e.target.value)}
              >
                {leads.map((lead) => (
                  <MenuItem key={lead.id} value={lead.id}>
                    {lead.full_name || `${lead.first_name} ${lead.last_name}` || "Lead"}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Title"
                fullWidth
                size="small"
                value={form.title}
                onChange={(e) => handleChange("title", e.target.value)}
              />

              <TextField
                label="Notes"
                multiline
                minRows={4}
                fullWidth
                size="small"
                value={form.notes}
                onChange={(e) => handleChange("notes", e.target.value)}
              />
            </Box>

            {/* Right Column - add padding here instead */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                overflowY: "auto",
                p: 2, // Padding on the column itself
              }}
            >
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: "primary.main" }}>
                Schedule
              </Typography>
              
              <TextField
                label="Due Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
                value={form.due_date}
                onChange={(e) => handleChange("due_date", e.target.value)}
              />

              <TextField
                select
                fullWidth
                label="Priority"
                size="small"
                value={form.priority}
                onChange={(e) => handleChange("priority", e.target.value)}
              >
                <MenuItem value="Low">Low</MenuItem>
                <MenuItem value="Normal">Normal</MenuItem>
                <MenuItem value="High">High</MenuItem>
              </TextField>
            </Box>
          </>
        ) : (
          // Portrait mode - single column
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              select
              fullWidth
              label="Lead"
              value={form.lead}
              onChange={(e) => handleChange("lead", e.target.value)}
            >
              {leads.map((lead) => (
                <MenuItem key={lead.id} value={lead.id}>
                  {lead.full_name || `${lead.first_name} ${lead.last_name}` || "Lead"}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Title"
              fullWidth
              value={form.title}
              onChange={(e) => handleChange("title", e.target.value)}
            />

            <TextField
              label="Notes"
              multiline
              minRows={3}
              fullWidth
              value={form.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
            />

            <TextField
              label="Due Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={form.due_date}
              onChange={(e) => handleChange("due_date", e.target.value)}
            />

           
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave}>
          Save Follow-Up
        </Button>
      </DialogActions>
    </CrmDialog>
  );
}
